import React, { useState } from 'react';
import { Badge, Popover, Drawer, Button } from 'antd';
import { BellOutlined, ExpandOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../stores/useAppStore';
import { useFeatureFlag } from '../../hooks/useFeatureFlags';
import { NotificationCenter } from './NotificationCenter';

interface NotificationBellProps {
  mode?: 'popover' | 'drawer';
  showPreviewCount?: number;
  enableQuickActions?: boolean;
  className?: string;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  mode = 'popover',
  showPreviewCount = 5,
  enableQuickActions = true,
  className = '',
}) => {
  const navigate = useNavigate();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const notifications = useAppStore((state) => state.notifications);
  const disabledTypes = useAppStore((state) => state.settings.disabledTypes);
  const { isEnabled: notificationCenterEnabled } = useFeatureFlag('notification_center');

  const unreadCount = notifications.filter(
    (n) => !n.read && !disabledTypes.includes(n.type)
  ).length;

  if (!notificationCenterEnabled) {
    return null;
  }

  const handleBellClick = () => {
    if (mode === 'popover') {
      setPopoverOpen(!popoverOpen);
    } else {
      setDrawerOpen(true);
    }
  };

  const handleViewAll = () => {
    setPopoverOpen(false);
    setDrawerOpen(false);
    navigate('/notifications');
  };

  const handleClosePopover = () => {
    setPopoverOpen(false);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const popoverContent = (
    <div style={{ width: 380 }}>
      <NotificationCenter
        mode="popover"
        maxHeight={450}
        showFilters={false}
        showActions={enableQuickActions}
        onClose={handleClosePopover}
      />
      
      {notifications.length > 0 && (
        <div style={{ 
          borderTop: '1px solid var(--color-border-primary)',
          padding: '12px 16px',
          background: 'var(--color-bg-primary)',
        }}>
          <Button
            type="primary"
            icon={<ExpandOutlined />}
            onClick={handleViewAll}
            block
            ghost
            style={{ borderRadius: '8px' }}
          >
            {notifications.length > showPreviewCount 
              ? `Показать все (${notifications.length})` 
              : 'Открыть страницу уведомлений'}
          </Button>
        </div>
      )}
    </div>
  );

  const bellButton = (
    <motion.button
      className={`header-button notification-bell ${className}`}
      onClick={handleBellClick}
      title="Уведомления"
      data-tour-id="notifications-bell"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      animate={unreadCount > 0 ? {
        rotate: [0, -10, 10, -10, 0],
        transition: { duration: 0.5, repeat: Infinity, repeatDelay: 3 }
      } : {}}
    >
      <Badge count={unreadCount} overflowCount={99} offset={[-2, 2]}>
        <BellOutlined style={{ fontSize: 18, color: 'white' }} />
      </Badge>
    </motion.button>
  );

  if (mode === 'popover') {
    return (
      <Popover
        content={popoverContent}
        trigger="click"
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        placement="bottomRight"
        overlayClassName="notification-bell-popover"
      >
        {bellButton}
      </Popover>
    );
  }

  return (
    <>
      {bellButton}
      <Drawer
        title="Уведомления"
        placement="right"
        onClose={handleCloseDrawer}
        open={drawerOpen}
        width={400}
        className="notification-bell-drawer"
      >
        <NotificationCenter
          mode="drawer"
          showFilters={true}
          showActions={enableQuickActions}
          onClose={handleCloseDrawer}
        />
      </Drawer>
    </>
  );
};


